<script setup lang="ts">
import PdsButton from '@/components/pds/PdsButton.vue';
import PdsField from '@/components/pds/PdsField.vue';
import PdsModal from '@/components/pds/PdsModal.vue';
import PdsSegmented from '@/components/pds/PdsSegmented.vue';
import { computed, reactive, ref } from 'vue';
import { useDevices } from '../devices/useDevices';
import type { PdsDevice } from '../devices/types';
import {
  agentAddressError,
  buildAgentUrl,
  DEFAULT_AGENT_PORT,
  parseAgentUrl,
  type AgentAddress,
} from '../print/agent.address';
import { detectOs } from '../print/agent.download';
import { agentUrlIssue } from '../print/agent.url';

const props = defineProps<{ device: PdsDevice }>();
const emit = defineEmits<{ close: []; connected: [] }>();

const { connectAgentAt, agentUrlFor } = useDevices();

// L'adresse déclarée pour l'agent, à vérifier ou à compléter : un port absent reçoit celui par défaut de l'agent.
const declared = parseAgentUrl(agentUrlFor(props.device));
const address = reactive<AgentAddress>({
  ...declared,
  port: declared.port || DEFAULT_AGENT_PORT,
});

const SCHEMES = [
  { value: 'http' as const, label: 'http' },
  { value: 'https' as const, label: 'https' },
];

const busy = ref(false);
const failure = ref('');

const target = computed(() => buildAgentUrl(address));
const error = computed(() => agentAddressError(address));
const warning = computed(() =>
  error.value
    ? null
    : agentUrlIssue(target.value, {
        pageProtocol: window.location.protocol,
        isMobile: detectOs(navigator) === null,
      }),
);

// Un seul geste : l'agent répond à cette adresse, ou il ne répond pas et on propose de le chercher.
const go = async () => {
  if (error.value || busy.value) return;
  busy.value = true;
  failure.value = '';
  const message = await connectAgentAt(props.device, target.value);
  busy.value = false;
  if (message) failure.value = message;
  else emit('connected');
};

// La page d'imprimante reprend l'adresse saisie et lance la recherche réseau d'elle-même.
const searchLink = computed(
  () => `/reglages/imprimante?scan=1&agent=${encodeURIComponent(target.value)}`,
);
</script>

<template>
  <PdsModal size="sm" @close="emit('close')">
    <template #title>Compléter l’adresse de l’agent</template>
    <template #subtitle>{{ device.label }}</template>

    <form class="flex flex-col gap-4 px-5 py-6" @submit.prevent="go">
      <p class="text-encre-douce text-[14px] leading-[1.4]">
        L’agent d’impression tourne sur le PC relié à l’imprimante. Vérifiez son
        adresse IP et son port, ou complétez-les, puis continuez pour vous y
        connecter.
      </p>

      <PdsSegmented v-model="address.scheme" :options="SCHEMES" />

      <div class="grid grid-cols-[1fr_120px] gap-3">
        <PdsField
          id="agent-host"
          v-model="address.host"
          label="Adresse IP de l’agent"
          placeholder="192.168.1.10"
          :invalid="!!error && !!address.host"
        />
        <PdsField id="agent-port" v-model="address.port" label="Port" />
      </div>

      <p
        v-if="error && address.host"
        role="alert"
        class="text-alerte-texte text-[12px] leading-[17px]"
      >
        {{ error }}
      </p>
      <p
        v-else-if="warning"
        class="text-encre-douce text-[12px] leading-[17px]"
        data-testid="agent-warning"
      >
        {{ warning.text }}
      </p>

      <div
        v-if="failure"
        role="alert"
        class="bg-alerte-doux text-alerte-texte flex flex-col gap-1 rounded-[16px] p-4 text-[14px] leading-[1.4]"
        data-testid="agent-failure"
      >
        <p class="font-semibold">Connexion impossible</p>
        <p>{{ failure }}</p>
        <p class="text-[12px] leading-[17px]">
          Vérifiez que l’agent est démarré, ou recherchez-le sur le réseau.
        </p>
      </div>
    </form>

    <template #footer>
      <PdsButton variant="second" fit @click="emit('close')">Annuler</PdsButton>
      <PdsButton
        v-if="failure"
        variant="second"
        fit
        :to="searchLink"
        @click="emit('close')"
      >
        Rechercher sur le réseau
      </PdsButton>
      <PdsButton
        fit
        class="sm:ml-auto"
        :loading="busy"
        :disabled="!!error"
        @click="go"
      >
        {{ failure ? 'Réessayer' : 'Continuer' }}
      </PdsButton>
    </template>
  </PdsModal>
</template>
